import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import axios from "axios";
import { useAuth } from "../context/AuthContext";
import {
  Clock, Plus, Trash2, Loader2, Play, Pause, FileText,
  Calendar, Building2, X, ChevronRight, Zap
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "../components/ui/select";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const fmt = (a) => new Intl.NumberFormat("en-IN", { style: "currency", currency: "INR", minimumFractionDigits: 0 }).format(a || 0);

export default function RecurringInvoicesPage() {
  const { isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const [recurring, setRecurring] = useState([]);
  const [companies, setCompanies] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [generating, setGenerating] = useState(null);
  const [toggling, setToggling] = useState(null);

  const [form, setForm] = useState({
    company_id: "", customer_name: "", customer_gst: "", customer_email: "",
    frequency: "monthly", start_date: new Date().toISOString().slice(0, 10),
    end_date: "", notes: "",
    items: [{ name: "", description: "", hsn_code: "", quantity: 1, unit_price: 0, gst_percent: 18 }],
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    if (!authLoading && isAuthenticated) fetchData();
    else if (!authLoading) navigate("/auth");
  }, [isAuthenticated, authLoading]);

  const fetchData = async () => {
    try {
      const [recRes, compRes, custRes] = await Promise.all([
        axios.get(`${API}/recurring/list`, { withCredentials: true }),
        axios.get(`${API}/companies`, { withCredentials: true }),
        axios.get(`${API}/customers`, { withCredentials: true }).catch(() => ({ data: { customers: [] } })),
      ]);
      setRecurring(recRes.data.recurring || []);
      setCompanies(compRes.data.companies || []);
      setCustomers(custRes.data.customers || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!form.company_id) { toast.error("Select a company"); return; }
    if (!form.customer_name.trim()) { toast.error("Enter customer name"); return; }
    if (!form.items[0]?.name?.trim()) { toast.error("Add at least one item"); return; }
    setCreating(true);
    try {
      const res = await axios.post(`${API}/recurring/create`, form, { withCredentials: true });
      setRecurring(prev => [res.data, ...prev]);
      setShowCreate(false);
      setForm({
        company_id: form.company_id, customer_name: "", customer_gst: "", customer_email: "",
        frequency: "monthly", start_date: new Date().toISOString().slice(0, 10),
        end_date: "", notes: "",
        items: [{ name: "", description: "", hsn_code: "", quantity: 1, unit_price: 0, gst_percent: 18 }],
      });
      toast.success("Recurring invoice template created");
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create");
    } finally {
      setCreating(false);
    }
  };

  const handleToggle = async (id) => {
    setToggling(id);
    try {
      const res = await axios.put(`${API}/recurring/${id}/toggle`, {}, { withCredentials: true });
      setRecurring(prev => prev.map(r => r.recurring_id === id ? { ...r, is_active: res.data.is_active } : r));
      toast.success(res.data.is_active ? "Activated" : "Paused");
    } catch {
      toast.error("Failed to toggle");
    } finally {
      setToggling(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this recurring template?")) return;
    try {
      await axios.delete(`${API}/recurring/${id}`, { withCredentials: true });
      setRecurring(prev => prev.filter(r => r.recurring_id !== id));
      toast.success("Deleted");
    } catch {
      toast.error("Failed to delete");
    }
  };

  const handleGenerate = async (id) => {
    setGenerating(id);
    try {
      const res = await axios.post(`${API}/recurring/${id}/generate`, {}, { withCredentials: true });
      toast.success("Invoice generated! ID: " + res.data.invoice_id);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to generate");
    } finally {
      setGenerating(null);
    }
  };

  const updateItem = (idx, field, value) => {
    setForm(prev => {
      const items = [...prev.items];
      items[idx] = { ...items[idx], [field]: value };
      return { ...prev, items };
    });
  };

  const addItem = () => {
    setForm(prev => ({
      ...prev,
      items: [...prev.items, { name: "", description: "", hsn_code: "", quantity: 1, unit_price: 0, gst_percent: 18 }],
    }));
  };

  const removeItem = (idx) => {
    if (form.items.length <= 1) return;
    setForm(prev => ({ ...prev, items: prev.items.filter((_, i) => i !== idx) }));
  };

  const itemTotal = (item) => {
    const base = (item.quantity || 0) * (item.unit_price || 0);
    return base + base * ((item.gst_percent || 0) / 100);
  };

  if (loading || authLoading) {
    return <div className="min-h-screen bg-[#F9FAFB] flex items-center justify-center"><Loader2 className="w-8 h-8 animate-spin text-[#2563EB]" /></div>;
  }

  return (
    <div className="min-h-screen bg-[#F9FAFB] py-6 px-4" data-testid="recurring-page">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 mb-6">
          <div>
            <h1 className="text-2xl font-bold text-slate-900">Recurring Invoices</h1>
            <p className="text-sm text-slate-500">Auto-generate invoices for retainer clients</p>
          </div>
          <Button onClick={() => setShowCreate(true)} className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white text-sm" data-testid="create-recurring-btn">
            <Plus className="w-4 h-4 mr-1.5" /> New Template
          </Button>
        </div>

        {/* Create Form Modal */}
        <AnimatePresence>
          {showCreate && (
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="fixed inset-0 z-50 bg-black/40 flex items-start justify-center pt-12 px-4 overflow-y-auto">
              <motion.div initial={{ y: 20, opacity: 0 }} animate={{ y: 0, opacity: 1 }} exit={{ y: 20, opacity: 0 }}
                className="bg-white rounded-2xl shadow-2xl w-full max-w-2xl mb-12" data-testid="create-recurring-modal">
                <div className="flex items-center justify-between p-5 border-b border-slate-100">
                  <h2 className="text-lg font-bold text-slate-900">New Recurring Template</h2>
                  <button onClick={() => setShowCreate(false)} className="p-1.5 rounded-lg hover:bg-slate-100"><X className="w-5 h-5" /></button>
                </div>
                <div className="p-5 space-y-4">
                  {/* Company + Customer */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div>
                      <label className="text-xs font-medium text-slate-500 mb-1 block">Company</label>
                      <Select value={form.company_id} onValueChange={(v) => setForm(p => ({ ...p, company_id: v }))}>
                        <SelectTrigger data-testid="rec-company"><SelectValue placeholder="Select company" /></SelectTrigger>
                        <SelectContent>
                          {companies.map(c => <SelectItem key={c.company_id} value={c.company_id}>{c.company_name}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-500 mb-1 block">Customer Name</label>
                      <Input value={form.customer_name} onChange={e => setForm(p => ({ ...p, customer_name: e.target.value }))} placeholder="Customer name" data-testid="rec-customer-name" />
                    </div>
                  </div>
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                    <div>
                      <label className="text-xs font-medium text-slate-500 mb-1 block">Frequency</label>
                      <Select value={form.frequency} onValueChange={(v) => setForm(p => ({ ...p, frequency: v }))}>
                        <SelectTrigger data-testid="rec-frequency"><SelectValue /></SelectTrigger>
                        <SelectContent>
                          <SelectItem value="monthly">Monthly</SelectItem>
                          <SelectItem value="quarterly">Quarterly</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-500 mb-1 block">Start Date</label>
                      <Input type="date" value={form.start_date} onChange={e => setForm(p => ({ ...p, start_date: e.target.value }))} data-testid="rec-start-date" />
                    </div>
                    <div>
                      <label className="text-xs font-medium text-slate-500 mb-1 block">End Date (optional)</label>
                      <Input type="date" value={form.end_date} onChange={e => setForm(p => ({ ...p, end_date: e.target.value }))} data-testid="rec-end-date" />
                    </div>
                  </div>

                  {/* Items */}
                  <div>
                    <label className="text-xs font-medium text-slate-500 mb-2 block">Line Items</label>
                    {form.items.map((item, idx) => (
                      <div key={idx} className="flex items-start gap-2 mb-2">
                        <Input placeholder="Item name" value={item.name} onChange={e => updateItem(idx, "name", e.target.value)} className="flex-1" data-testid={`rec-item-name-${idx}`} />
                        <Input type="number" placeholder="Qty" value={item.quantity} onChange={e => updateItem(idx, "quantity", parseFloat(e.target.value) || 0)} className="w-20" data-testid={`rec-item-qty-${idx}`} />
                        <Input type="number" placeholder="Price" value={item.unit_price} onChange={e => updateItem(idx, "unit_price", parseFloat(e.target.value) || 0)} className="w-28" data-testid={`rec-item-price-${idx}`} />
                        <Input type="number" placeholder="GST %" value={item.gst_percent} onChange={e => updateItem(idx, "gst_percent", parseFloat(e.target.value) || 0)} className="w-20" />
                        <span className="text-sm font-medium text-slate-700 w-24 text-right pt-2">{fmt(itemTotal(item))}</span>
                        {form.items.length > 1 && (
                          <button onClick={() => removeItem(idx)} className="p-2 text-slate-400 hover:text-red-500"><X className="w-4 h-4" /></button>
                        )}
                      </div>
                    ))}
                    <Button variant="ghost" size="sm" onClick={addItem} className="text-[#2563EB] text-xs mt-1" data-testid="rec-add-item">
                      <Plus className="w-3 h-3 mr-1" /> Add Item
                    </Button>
                  </div>

                  <div>
                    <label className="text-xs font-medium text-slate-500 mb-1 block">Notes (optional)</label>
                    <Input value={form.notes} onChange={e => setForm(p => ({ ...p, notes: e.target.value }))} placeholder="Monthly retainer for..." data-testid="rec-notes" />
                  </div>
                </div>
                <div className="p-5 border-t border-slate-100 flex justify-end gap-2">
                  <Button variant="outline" onClick={() => setShowCreate(false)}>Cancel</Button>
                  <Button onClick={handleCreate} disabled={creating} className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white" data-testid="rec-save-btn">
                    {creating ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <Plus className="w-4 h-4 mr-1" />} Create Template
                  </Button>
                </div>
              </motion.div>
            </motion.div>
          )}
        </AnimatePresence>

        {/* List */}
        {recurring.length === 0 ? (
          <div className="bg-white rounded-2xl border border-slate-100 shadow-sm p-12 text-center" data-testid="recurring-empty">
            <Clock className="w-12 h-12 text-slate-300 mx-auto mb-4" />
            <h3 className="text-lg font-bold text-slate-900 mb-1">No recurring templates</h3>
            <p className="text-slate-500 mb-4">Set up automatic invoicing for your retainer clients.</p>
            <Button onClick={() => setShowCreate(true)} className="bg-[#2563EB] hover:bg-[#1D4ED8] text-white">
              <Plus className="w-4 h-4 mr-1" /> Create First Template
            </Button>
          </div>
        ) : (
          <div className="space-y-3">
            {recurring.map((rec) => {
              const total = (rec.items || []).reduce((s, it) => {
                const base = (it.quantity || 0) * (it.unit_price || 0);
                return s + base + base * ((it.gst_percent || 0) / 100);
              }, 0);

              return (
                <motion.div
                  key={rec.recurring_id}
                  initial={{ opacity: 0, y: 4 }}
                  animate={{ opacity: 1, y: 0 }}
                  className="bg-white rounded-xl border border-slate-100 shadow-sm p-4 hover:shadow-md transition-all"
                  data-testid={`recurring-row-${rec.recurring_id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-xl flex items-center justify-center flex-shrink-0 ${rec.is_active ? "bg-teal-50 text-teal-600" : "bg-slate-100 text-slate-400"}`}>
                      <Clock className="w-5 h-5" />
                    </div>

                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="font-semibold text-slate-900 text-sm">{rec.customer_name}</span>
                        <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${rec.is_active ? "bg-teal-50 text-teal-700" : "bg-slate-100 text-slate-500"}`}>
                          {rec.is_active ? "Active" : "Paused"}
                        </span>
                        <span className="text-xs text-slate-400 capitalize">{rec.frequency}</span>
                      </div>
                      <div className="flex items-center gap-3 text-xs text-slate-500">
                        <span>Next: {rec.next_due || "—"}</span>
                        <span>Generated: {rec.generated_count || 0}</span>
                        <span>{(rec.items || []).length} item{(rec.items || []).length !== 1 ? "s" : ""}</span>
                      </div>
                    </div>

                    <span className="text-base font-bold text-slate-900 min-w-[90px] text-right">{fmt(total)}</span>

                    <div className="flex items-center gap-1">
                      <button
                        onClick={() => handleGenerate(rec.recurring_id)}
                        disabled={generating === rec.recurring_id}
                        className="p-1.5 rounded-lg hover:bg-blue-50 text-slate-400 hover:text-blue-600 transition-colors"
                        title="Generate invoice now"
                        data-testid={`generate-${rec.recurring_id}`}
                      >
                        {generating === rec.recurring_id ? <Loader2 className="w-4 h-4 animate-spin" /> : <Zap className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => handleToggle(rec.recurring_id)}
                        disabled={toggling === rec.recurring_id}
                        className="p-1.5 rounded-lg hover:bg-slate-100 text-slate-400 hover:text-slate-700 transition-colors"
                        title={rec.is_active ? "Pause" : "Activate"}
                        data-testid={`toggle-${rec.recurring_id}`}
                      >
                        {toggling === rec.recurring_id ? <Loader2 className="w-4 h-4 animate-spin" /> : rec.is_active ? <Pause className="w-4 h-4" /> : <Play className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => handleDelete(rec.recurring_id)}
                        className="p-1.5 rounded-lg hover:bg-red-50 text-slate-400 hover:text-red-500 transition-colors"
                        title="Delete"
                        data-testid={`delete-recurring-${rec.recurring_id}`}
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
